import moment from 'moment-timezone';

// Debug flag for DST detection - set to true for debugging
export const DEBUG_DST = false;

export const TIMEZONE_MAP: Record<string, string> = {
  DE: 'Europe/Berlin',
  HK: 'Asia/Hong_Kong',
  NZ: 'Pacific/Auckland',
  ID: 'Asia/Jakarta',
  KR: 'Asia/Seoul',
  MY: 'Asia/Kuala_Lumpur',
  CN: 'Asia/Shanghai',
  US: 'America/Los_Angeles',
  UK: 'Europe/London',
  AU: 'Australia/Sydney',
  PH: 'Asia/Manila',
  SG: 'Asia/Singapore',
  TH: 'Asia/Bangkok',
  TW: 'Asia/Taipei',
  VN: 'Asia/Ho_Chi_Minh',
};

// More relaxed ISO date detection - covers more valid ISO variants
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d{1,3})?(Z|[+-]\d{2}:?\d{2})$/;

// Convert timezone with clear semantics
// isEncoding=true: Display same instant in target timezone (preserves instant)
// isEncoding=false: Convert target timezone display back to UTC (preserves instant)
function convertTimezone(dateStr: string | Date, targetCountry: string, isEncoding: boolean): string {
  const innerDateStr = typeof dateStr === 'string' ? dateStr : dateStr.toISOString();

  // Use more relaxed detection or trust moment's parsing
  if (!ISO_DATE_REGEX.test(innerDateStr)) {
    // Try parsing anyway - moment is quite forgiving
    const testParse = moment.parseZone(innerDateStr);
    if (!testParse.isValid()) return innerDateStr;
  }

  const timezone = TIMEZONE_MAP[targetCountry.toUpperCase()];
  if (!timezone) return innerDateStr;

  if (isEncoding) {
    // Same instant, display in target timezone
    const parsed = moment.parseZone(innerDateStr);
    if (!parsed.isValid()) return innerDateStr;
    return parsed.tz(timezone).toISOString();
  } else {
    // Convert from target timezone display back to UTC
    const parsed = moment.parseZone(innerDateStr);
    if (!parsed.isValid()) return innerDateStr;
    return parsed.utc().toISOString();
  }
}

// Traverse the obj
function traverseBody<T>(obj: T, country: string, isEncoding: boolean): T {
  if (obj === null || obj === undefined) return obj;

  if (typeof obj === 'string') {
    if (ISO_DATE_REGEX.test(obj)) {
      return convertTimezone(obj, country, isEncoding) as unknown as T;
    }
    return obj;
  }

  if (obj instanceof Date) {
    return convertTimezone(obj, country, isEncoding) as unknown as T;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => traverseBody(item, country, isEncoding)) as unknown as T;
  }

  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = traverseBody(value, country, isEncoding);
    }
    return result as T;
  }

  return obj;
}

export function localTimeToMarketTime<T>(body: T, country: string): T {
  const result = traverseBody(body, country, true);
  if (result === null || result === undefined) {
    throw new Error('Invalid body: result cannot be null or undefined');
  }
  return result as T;
}

export function marketTimeToLocalTime<T>(body: T, country: string): T {
  const result = traverseBody(body, country, false);
  if (result === null || result === undefined) {
    throw new Error('Invalid body: result cannot be null or undefined');
  }
  return result as T;
}

export function isBetweenDST(date: string, country: string): boolean {
  const timezone = TIMEZONE_MAP[country.toUpperCase()];
  if (!timezone) return false;

  // Parse the date
  const momentDate = moment.tz(date, timezone);
  if (!momentDate.isValid()) return false;

  // Use moment's built-in isDST() method
  return momentDate.isDST();
}

export const getSameGMTOffset = (date1: string, date2: string, country: string): boolean => {
  const timezone = TIMEZONE_MAP[country.toUpperCase()];
  if (!timezone) return false;

  const momentDate1 = moment.tz(date1, timezone);
  const momentDate2 = moment.tz(date2, timezone);

  return momentDate1.utcOffset() === momentDate2.utcOffset();
};

export const getGMTOffsetFormat = (date: string, country?: string): string => {
  if (!country) return '';

  const timezone = TIMEZONE_MAP[country.toUpperCase()];
  if (!timezone) return '';

  const momentDate = moment.tz(date, timezone);
  if (!momentDate.isValid()) return '';

  const offsetInMinutes = momentDate.utcOffset();
  const hours = Math.floor(Math.abs(offsetInMinutes) / 60);
  const minutes = Math.abs(offsetInMinutes) % 60;
  const sign = offsetInMinutes >= 0 ? '+' : '-';

  return `GMT${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Function to find DST transition dates within a given date range
export const findDSTTransitionInRange = (
  startDate: string,
  endDate: string,
  country: string
): { hasDSTTransition: boolean; transitionDate?: string; transitionType?: 'start' | 'end' } => {
  const timezone = TIMEZONE_MAP[country.toUpperCase()];
  if (!timezone) return { hasDSTTransition: false };

  const start = moment.tz(startDate, timezone);
  const end = moment.tz(endDate, timezone);

  if (!start.isValid() || !end.isValid()) {
    return { hasDSTTransition: false };
  }

  // Check each day in the range for offset changes
  const currentDate = start.clone();
  let previousDate = currentDate.clone();
  let previousOffset = currentDate.utcOffset();

  currentDate.add(1, 'day');

  while (currentDate.isSameOrBefore(end)) {
    const currentOffset = currentDate.utcOffset();

    // If offset changed, we found a DST transition
    if (currentOffset !== previousOffset) {
      const transitionType = currentOffset > previousOffset ? 'start' : 'end';

      // The transition happened between previousDate and currentDate
      // Report the date when the transition takes effect
      const transitionDate = transitionType === 'end' ? previousDate : currentDate;

      console.log(
        `DST transition detected between ${previousDate.format('YYYY-MM-DD')} and ${currentDate.format(
          'YYYY-MM-DD'
        )} in ${timezone}:`,
        {
          previousOffset: previousOffset / 60,
          currentOffset: currentOffset / 60,
          transitionType,
          reportingDate: transitionDate.format('YYYY-MM-DD'),
        }
      );

      return {
        hasDSTTransition: true,
        transitionDate: transitionDate.format('YYYY-MM-DD'),
        transitionType,
      };
    }

    previousDate = currentDate.clone();
    previousOffset = currentOffset;
    currentDate.add(1, 'day');
  }

  return { hasDSTTransition: false };
};

// Function to get the user's local timezone
export const getUserLocalTimezone = (): string => {
  return moment.tz.guess();
};

/**
 * Convert any timezone identifier (IANA name like `"Australia/Sydney"`, or
 * an already-formatted `"UTC+8"` / `"GMT+8"` token) into a compact
 * UTC-offset label suitable for chip-style display, e.g. `"UTC+11"` or
 * `"UTC+5:30"`. Handles DST automatically via moment-timezone — Sydney
 * reads `UTC+11` in summer and `UTC+10` in winter without extra logic.
 *
 * Returns an empty string for null/undefined input. Falls back to the
 * original string when the IANA name is unparseable, so the caller
 * still has something to render.
 */
export const getOffsetForTimezone = (timezone: string | null | undefined): string => {
  if (!timezone) return '';
  // Already in `UTC±N` or `GMT±N` form — return as-is so the chip stays
  // stable when the upstream API has already converted it.
  if (/^(UTC|GMT)[+-]/.test(timezone)) return timezone;
  const tz = moment.tz(timezone);
  if (!tz.isValid()) return timezone;
  const offsetMinutes = tz.utcOffset();
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;
  const sign = offsetMinutes >= 0 ? '+' : '-';
  return minutes === 0 ? `UTC${sign}${hours}` : `UTC${sign}${hours}:${String(minutes).padStart(2, '0')}`;
};

// Helper function to find precise DST transition time using binary search (hour precision)
const findPreciseDSTTransition = (
  dayBefore: moment.Moment,
  dayAfter: moment.Moment,
  timezone: string
): moment.Moment => {
  // Start with the beginning of the transition day (dayBefore)
  let left = dayBefore.clone().startOf('day');
  let right = dayAfter.clone().startOf('day');

  const leftIsDST = left.isDST();

  // Binary search by hour to find transition point
  while (right.diff(left, 'hours') > 1) {
    const mid = left.clone().add(Math.floor(right.diff(left, 'hours') / 2), 'hours');
    const midIsDST = mid.isDST();

    if (midIsDST === leftIsDST) {
      left = mid;
    } else {
      right = mid;
    }
  }

  return left;
};

// Function to check if user's local timezone has DST and if the selected range includes DST transition
export const checkUserLocalDSTInRange = (
  startDate: string,
  endDate: string,
  testMode?: boolean,
  testTimezone?: string
): {
  hasDSTTransition: boolean;
  transitionDate?: string;
  transitionType?: 'start' | 'end';
  userTimezone?: string;
  previousOffset?: number;
  currentOffset?: number;
} => {
  // Use test timezone in development or test mode, otherwise use user's actual timezone
  const userTimezone = testMode && testTimezone ? testTimezone : getUserLocalTimezone();

  const start = moment.tz(startDate, userTimezone);
  const end = moment.tz(endDate, userTimezone);

  if (!start.isValid() || !end.isValid()) {
    return { hasDSTTransition: false, userTimezone };
  }

  // Debug: Log the timezone and date range
  if (DEBUG_DST && process.env.NODE_ENV === 'development') {
    console.log(`DST Detection for ${userTimezone}:`, {
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
    });
  }

  // First pass: find which day has DST change (daily granularity)
  const currentDate = start.clone();
  let previousDate = currentDate.clone();
  let previousIsDST = currentDate.isDST();

  currentDate.add(1, 'day');

  while (currentDate.isSameOrBefore(end)) {
    const currentIsDST = currentDate.isDST();

    // If DST status changed, we found a day with DST transition
    if (currentIsDST !== previousIsDST) {
      const transitionType = currentIsDST ? 'start' : 'end';

      // Second pass: use binary search to find precise transition time within the day
      const transitionDay = findPreciseDSTTransition(previousDate, currentDate, userTimezone);

      // Get offset information for the toast
      const beforeTransition = transitionDay.clone().subtract(1, 'hour');
      const afterTransition = transitionDay.clone().add(1, 'hour');
      const previousOffset = beforeTransition.utcOffset();
      const currentOffset = afterTransition.utcOffset();

      // Debug: Log the transition details
      if (DEBUG_DST && process.env.NODE_ENV === 'development') {
        console.log(`DST transition found using binary search:`, {
          transitionType,
          transitionDay: transitionDay.format('YYYY-MM-DD HH:mm'),
          previousIsDST,
          currentIsDST,
          previousOffset: previousOffset / 60,
          currentOffset: currentOffset / 60,
          offsetChange: (currentOffset - previousOffset) / 60,
        });
      }

      return {
        hasDSTTransition: true,
        transitionDate: transitionDay.format('YYYY-MM-DD'),
        transitionType,
        userTimezone,
        previousOffset: previousOffset / 60, // Convert to hours
        currentOffset: currentOffset / 60, // Convert to hours
      };
    }

    previousDate = currentDate.clone();
    previousIsDST = currentIsDST;
    currentDate.add(1, 'day');
  }

  return { hasDSTTransition: false, userTimezone };
};

// ─── Market-time display (ported from needle-mission-control) ───────────────
// These mirror needle's `getGmtStr` / `formatDateRangeWithGmt` so the merchant
// portal and the Commercial Management page render the same instant identically.
// Deliberately NOT reusing getGMTOffsetFormat above (emits "GMT+08:00", we want
// "GMT+8") nor needle's getTimezoneDisplay (reads the offset at *now* rather than
// at the date, so it mislabels any DST market outside the current period).

/** Offset label for an instant in a market, e.g. "GMT+8" / "GMT+5:30". Empty when unmapped. */
export const getGmtStr = (dateTime: string, country?: string): string => {
  const timezone = country ? TIMEZONE_MAP[country.toUpperCase()] : undefined;
  if (!timezone) return '';
  // "+08:00" -> "+8", "+05:30" -> "+5:30", "-07:00" -> "-7". needle stops after
  // dropping ":00" and so renders "GMT+08" for single-digit offsets; strip the
  // leading zero too so every market reads consistently.
  const offset = moment(dateTime)
    .tz(timezone)
    .format('Z')
    .replace(':00', '')
    .replace(/^([+-])0(\d)/, '$1$2');
  return `GMT${offset}`;
};

/**
 * A date range rendered in its market's timezone with the offset appended.
 *
 * Country is uppercased before the TIMEZONE_MAP lookup: a lowercase code returns
 * undefined, moment silently falls back to the browser zone, and the displayed
 * time then contradicts its own offset label.
 *
 * A range spanning a DST transition gets an offset on each end, since one label
 * cannot describe both.
 */
export const formatDateRangeWithGmt = (
  startTime: string | null | undefined,
  endTime: string | null | undefined,
  fmt: string,
  country?: string
): string => {
  const timezone = country ? TIMEZONE_MAP[country.toUpperCase()] : undefined;
  const at = (t: string) => (timezone ? moment(t).tz(timezone).format(fmt) : moment(t).format(fmt));

  const start = startTime ? at(startTime) : null;
  const end = endTime ? at(endTime) : null;
  if (!start && !end) return '—';

  const startGmt = startTime ? getGmtStr(startTime, country) : '';
  const endGmt = endTime ? getGmtStr(endTime, country) : '';
  const suffix = (gmt: string) => (gmt ? ` (${gmt})` : '');

  if (!start) return `${end}${suffix(endGmt)}`;
  if (!end) return `${start}${suffix(startGmt)}`;
  if (startGmt === endGmt) return `${start} - ${end}${suffix(startGmt)}`;
  return `${start}${suffix(startGmt)} - ${end}${suffix(endGmt)}`;
};

/**
 * Range display for the wizards, whose values are `Date` objects standing in for a
 * market calendar day (browser-local midnight — see wizardMappers.fromIsoDatetime),
 * not instants.
 *
 * Such a Date must NOT be re-zoned: `moment(d).tz(market)` would shift the very day
 * the user picked. Read its calendar fields as-is and resolve the offset label from
 * the market instant that day-and-time represents.
 */
export const formatMarketDayRange = (
  start: Date | null | undefined,
  end: Date | null | undefined,
  country?: string,
  startTime?: string | null,
  endTime?: string | null
): string => {
  const zone = country ? TIMEZONE_MAP[country.toUpperCase()] : undefined;
  const ymd = (d: Date) => moment(d).format('YYYY-MM-DD');
  const show = (d: Date, t?: string | null) => `${moment(d).format('DD MMM YYYY')}${t ? ` ${t}` : ''}`;

  // Offset at the picked day in the market, so a DST-straddling range labels each end.
  const gmtAt = (d: Date, t?: string | null) => {
    if (!zone) return '';
    return getGmtStr(moment.tz(`${ymd(d)} ${t || '00:00'}`, 'YYYY-MM-DD HH:mm', zone).toISOString(), country);
  };

  if (!start && !end) return '—';
  const suffix = (g: string) => (g ? ` (${g})` : '');
  if (!start) return `${show(end as Date, endTime)}${suffix(gmtAt(end as Date, endTime))}`;
  if (!end) return `${show(start, startTime)}${suffix(gmtAt(start, startTime))}`;

  const gs = gmtAt(start, startTime);
  const ge = gmtAt(end, endTime);
  if (gs === ge) return `${show(start, startTime)} - ${show(end, endTime)}${suffix(gs)}`;
  return `${show(start, startTime)}${suffix(gs)} - ${show(end, endTime)}${suffix(ge)}`;
};
